import React, { useState, useEffect } from 'react';
import { getTenantId, getCourseBySlug, listModules, listLessonsByModule } from '../../repos/contentRepo';
import { DEFAULT_TENANT, DEFAULT_COURSE_SLUG } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const DebugData = () => {
  const [tenantId, setTenantId] = useState(null);
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Get tenant ID
      console.log('🔍 Getting tenant ID for:', DEFAULT_TENANT);
      const tId = await getTenantId();
      setTenantId(tId);
      console.log('✅ Tenant ID:', tId);

      // 2. Get course
      console.log('🔍 Getting course for:', DEFAULT_COURSE_SLUG);
      const c = await getCourseBySlug(tId, DEFAULT_COURSE_SLUG);
      setCourse(c);
      console.log('✅ Course:', c);

      // 3. Get modules
      console.log('🔍 Getting modules for course:', c.id);
      const ms = await listModules(tId, c.id);
      setModules(ms);
      console.log('✅ Modules:', ms);

    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadModuleLessons = async (moduleId) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Getting lessons for module:', moduleId);
      const ls = await listLessonsByModule(moduleId);
      setLessons(ls);
      setSelectedModule(modules.find(m => m.id === moduleId));
      console.log('✅ Lessons:', ls);

    } catch (err) {
      console.error('❌ Error loading lessons:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'info') => {
    // Simple toast implementation
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#dc2626' : '#059669'};
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      z-index: 9999;
      font-family: system-ui, sans-serif;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 5000);
  };

  if (error) {
    showToast(`Erro: ${error}`, 'error');
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">🔍 Debug de Dados</h1>
            <p className="text-muted-foreground">Diagnóstico completo da integração com Supabase</p>
          </div>

          {/* Environment Variables */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">🌍 Variáveis de Ambiente</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="font-medium">Tenant:</span> {DEFAULT_TENANT}
              </div>
              <div>
                <span className="font-medium">Course:</span> {DEFAULT_COURSE_SLUG}
              </div>
            </div>
          </div>

          {/* Data Status */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">📊 Status dos Dados</h2>
            
            {loading && (
              <div className="flex items-center space-x-2 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span>Carregando...</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <span className="font-medium">Tenant ID:</span> {tenantId ? `✅ ${tenantId}` : '❌ Não carregado'}
              </div>
              
              <div>
                <span className="font-medium">Course:</span> {course ? (
                  <div className="ml-4 mt-2">
                    <div>ID: {course.id}</div>
                    <div>Title: {course.title}</div>
                    <div>Status: {course.status}</div>
                  </div>
                ) : '❌ Não carregado'}
              </div>
              
              <div>
                <span className="font-medium">Modules:</span> {modules.length > 0 ? `✅ ${modules.length} módulos` : '❌ Nenhum módulo'}
              </div>
            </div>
          </div>

          {/* Modules List */}
          {modules.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">📚 Módulos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {modules.map((module) => (
                  <div
                    key={module.id}
                    className="border border-border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => loadModuleLessons(module.id)}
                  >
                    <h3 className="font-semibold text-foreground mb-2">{module.title}</h3>
                    <div className="text-sm text-muted-foreground">
                      <div>ID: {module.id}</div>
                      <div>Slug: {module.slug}</div>
                      <div>Status: {module.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lessons List */}
          {selectedModule && lessons.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                📖 Lições do Módulo: {selectedModule.title}
              </h2>
              <div className="space-y-3">
                {lessons.map((lesson) => (
                  <div key={lesson.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-foreground">{lesson.title}</h4>
                        <div className="text-sm text-muted-foreground">
                          ID: {lesson.id} | Order: {lesson.order} | Free: {lesson.is_free ? '✅' : '🔒'}
                        </div>
                      </div>
                      {!lesson.is_free && (
                        <Icon name="Lock" size={16} className="text-muted-foreground" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-4">
            <Button onClick={loadData} disabled={loading}>
              🔄 Recarregar Dados
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                console.log('🔍 Debug Info:', {
                  tenantId,
                  course,
                  modules,
                  selectedModule,
                  lessons
                });
                showToast('Informações logadas no console', 'info');
              }}
            >
              📝 Log no Console
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DebugData;








































